import { useQuery } from "@tanstack/react-query";
import { useIsGrandParent } from "src/hooks/useIsGrandParent";
import failedTxnService from "src/services/failedTxnService";

/**
 * Reads the per-company "Failed Transactions" flag for the LOGGED-IN user's own
 * company (set from Company → Settings by a super admin). JWT-keyed server-side,
 * so it needs no companyId and works for every company user. Resilient: defaults
 * to { failedTxnEnabled: false } on any failure, so it never blocks rendering.
 */
export const useCompanyFailedTxnConfig = () =>
  useQuery(["failedTxnConfig"], () => failedTxnService.getFailedTxnConfig(), {
    staleTime: 5 * 60 * 1000,
    placeholderData: { failedTxnEnabled: false },
  });

/**
 * Effective "is the Failed Transactions view on" for the logged-in user — the
 * single gate for the menu item and the page/route.
 *
 * The platform super admin — the Grand Parent company — ALWAYS has it,
 * independent of any company flag, since it operates across companies via the
 * team/company switcher. "Super admin" here means the Grand Parent company
 * specifically (companyType === "GP"); a Parent company's ROOT admin is a normal
 * company login and follows its OWN company's flag, exactly like every other
 * (company / team) login.
 */
export const useFailedTxnEnabled = () => {
  const isGrandParent = useIsGrandParent();
  const { data } = useCompanyFailedTxnConfig();
  return isGrandParent || data?.failedTxnEnabled === true;
};

export default useFailedTxnEnabled;
